import { useAuth } from './auth.context';

// Base API URL - use the environment variable to ensure requests go to the correct domain
// This is critical for sandbox/production environments where the frontend and API are on different domains
// In development, use the proxy to avoid CORS issues
const API_BASE_URL = import.meta.env.DEV 
  ? '' // Use empty string to leverage the Vite proxy (relative URLs)
  : (import.meta.env.VITE_API_URL || '');

// Log the API base URL during initialization to help with debugging
console.log(`API client initialized with base URL: ${API_BASE_URL}`);

/**
 * Global fetch wrapper to handle CORS issues
 * This overrides the global fetch function to ensure all requests use the Vite proxy in development
 */
const originalFetch = window.fetch;
window.fetch = function(input, init) {
  // Only modify requests in development
  if (import.meta.env.DEV) {
    let url = input.toString();
    
    // Check if this is an absolute URL to our API
    if (url.includes('localhost:8000') || 
        (import.meta.env.VITE_API_URL && url.includes(import.meta.env.VITE_API_URL))) {
      
      // Convert to a relative URL to use the Vite proxy
      console.log(`CORS protection: Converting ${url} to relative URL`);
      
      // Extract the path part after the domain
      let path = '';
      try {
        const urlObj = new URL(url);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {
        console.error('Error parsing URL:', e);
        path = url; // Fallback
      }
      
      // Use the relative path instead
      url = path;
      
      // Ensure credentials are included
      init = {
        ...init,
        credentials: 'include',
      };
      
      console.log(`CORS protection: Converted to ${url}`);
      return originalFetch(url, init);
    }
  }
  
  // For all other requests, use the original fetch
  return originalFetch(input, init);
};

/**
 * Creates a fetch request with authentication headers
 */
export const createAuthenticatedRequest = (
  token: string | null,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'GET',
  body?: any
): RequestInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Always use 'include' for credentials to ensure cookies are sent with cross-origin requests
  // This works with the CORS configuration in Kong that has credentials: true
  const requestOptions: RequestInit = {
    method,
    headers,
    credentials: 'include',
    // Add a signal with timeout to prevent hanging requests
    signal: AbortSignal.timeout(15000), // 15 second timeout
  };

  if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
    requestOptions.body = JSON.stringify(body);
  }

  return requestOptions;
};

/**
 * Handles API responses and errors
 */
export const handleApiResponse = async <T>(response: Response): Promise<T> => {
  console.log(`API response status: ${response.status} ${response.statusText} from ${response.url}`);
  
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage: string;
    let errorDetails: any = {};
    
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorJson.error || `Error: ${response.status}`;
      errorDetails = errorJson;
    } catch (e) {
      errorMessage = errorText || `Error: ${response.status}`;
    }
    
    console.error(`API error (${response.status}) from ${response.url}:`, errorDetails);
    
    // Create a more detailed error
    const error = new Error(errorMessage);
    (error as any).status = response.status;
    (error as any).url = response.url;
    (error as any).details = errorDetails;
    
    throw error;
  }
  
  // For 204 No Content responses, return an empty object
  if (response.status === 204) {
    return {} as T;
  }
  
  try {
    const data = await response.json();
    return data as T;
  } catch (error) {
    console.error(`Error parsing JSON response from ${response.url}:`, error);
    throw new Error(`Failed to parse API response as JSON: ${error}`);
  }
};

/**
 * API client for making authenticated requests
 */
export const apiClient = {
  get: async <T>(endpoint: string, token: string | null): Promise<T> => {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log(`API GET request to: ${url}`);
    const response = await fetch(url, createAuthenticatedRequest(token));
    return handleApiResponse<T>(response);
  },
  
  post: async <T>(endpoint: string, data: any, token: string | null): Promise<T> => {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log(`API POST request to: ${url}`, data);
    const response = await fetch(
      url,
      createAuthenticatedRequest(token, 'POST', data)
    );
    return handleApiResponse<T>(response);
  },
  
  put: async <T>(endpoint: string, data: any, token: string | null): Promise<T> => {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log(`API PUT request to: ${url}`, data);
    const response = await fetch(
      url,
      createAuthenticatedRequest(token, 'PUT', data)
    );
    return handleApiResponse<T>(response);
  },
  
  patch: async <T>(endpoint: string, data: any, token: string | null): Promise<T> => {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log(`API PATCH request to: ${url}`, data);
    const response = await fetch(
      url,
      createAuthenticatedRequest(token, 'PATCH', data)
    );
    return handleApiResponse<T>(response);
  },
  
  delete: async <T>(endpoint: string, token: string | null): Promise<T> => {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log(`API DELETE request to: ${url}`);
    const response = await fetch(
      url,
      createAuthenticatedRequest(token, 'DELETE')
    );
    return handleApiResponse<T>(response);
  }
};

/**
 * Hook to use the API client with authentication
 */
export const useApi = () => {
  const { getToken } = useAuth();
  
  return {
    get: <T>(endpoint: string) => apiClient.get<T>(endpoint, getToken()),
    post: <T>(endpoint: string, data: any) => apiClient.post<T>(endpoint, data, getToken()),
    put: <T>(endpoint: string, data: any) => apiClient.put<T>(endpoint, data, getToken()),
    patch: <T>(endpoint: string, data: any) => apiClient.patch<T>(endpoint, data, getToken()),
    delete: <T>(endpoint: string) => apiClient.delete<T>(endpoint, getToken())
  };
};